// Smooth-scrolls a section into view each time it advances to a NEW phase. Pass a boolean for a
// simple reveal, or a phase string so an async section gets a second push once content arrives.

import { useEffect, useRef } from 'react';

export const useScrollOnReveal = <T extends HTMLElement = HTMLDivElement>(phase: string | boolean) => {
  const ref = useRef<T>(null);
  const prev = useRef<string | boolean>(false);

  useEffect(() => {
    if (phase && phase !== prev.current && ref.current) {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      // 'nearest' scrolls only when the section isn't already in view — so the second push (loading→loaded)
      // and overlapping reveals don't yank an already-visible section, which reads as a flicker.
      ref.current.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
    }
    prev.current = phase;
  }, [phase]);

  return ref;
};

export const getScrollParent = (el: HTMLElement | null): HTMLElement | null => {
  let node = el?.parentElement ?? null;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return null;
};

export const scrollWizardToTop = (from: HTMLElement | null): void => {
  const parent = getScrollParent(from);
  if (parent) parent.scrollTo({ top: 0, behavior: 'auto' });
  else window.scrollTo({ top: 0 });
};
